/**
 * Word Form View
 *
 * Unified page for creating and editing words.
 * - Edit mode: /words/:word/:lang/edit
 * - Add mode: /words/new?listId=xxx&lang=xxx
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useInitialData } from '../../../contexts/InitialDataContext';
import {
  useWord,
  useCheckWordExists,
  useAddWordToList,
  useCreateWord,
  useUpdateWord,
} from '../services/wordsApi';
import {
  useGenerateAllPronunciations,
  useGenerateImage,
  useGenerateWordMetadata,
} from '../services/aiGenerationApi';
import type {
  PronunciationData,
  AddWordToListRequest,
  WordUpdateRequest,
} from '../types/word.types';

export function WordFormView() {
  const { word: wordParam, lang: langParam } = useParams<{ word: string; lang: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isEdit = Boolean(wordParam && langParam);
  const listId = searchParams.get('listId');
  const requestedCardId = searchParams.get('cardId') || undefined;
  const contextLanguage = searchParams.get('lang') || '';

  // Form state
  const [word, setWord] = useState('');
  const [languageCode, setLanguageCode] = useState(contextLanguage);
  const [imageUrl, setImageUrl] = useState('');
  const [exampleSentence, setExampleSentence] = useState('');
  const [senseRank, setSenseRank] = useState<number | null>(null);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [exampleTranslations, setExampleTranslations] = useState<Record<string, string>>({});
  const [pronunciations, setPronunciations] = useState<Record<string, PronunciationData>>({});

  // For adding new entries
  const [newTransLang, setNewTransLang] = useState('');
  const [newTransValue, setNewTransValue] = useState('');
  const [newPronLocale, setNewPronLocale] = useState('');
  const [newPronIpa, setNewPronIpa] = useState('');
  const [checkEnabled, setCheckEnabled] = useState(false);

  const { state: initialDataState } = useInitialData();
  const allLanguages = initialDataState.data?.languages ?? [];
  const languages = allLanguages.filter((l) => l.code !== 'zxx' && l.code !== 'other');
  const locales = initialDataState.data?.locales ?? [];

  // Helper to get language name from code
  const getLanguageName = (code: string) =>
    allLanguages.find((l) => l.code === code)?.name || code.toUpperCase();

  // API hooks
  const {
    data: existingWord,
    isLoading,
    error,
  } = useWord(
    isEdit ? decodeURIComponent(wordParam!) : '',
    isEdit ? langParam! : '',
    isEdit ? requestedCardId : undefined
  );
  const addWordToList = useAddWordToList();
  const createWord = useCreateWord();
  const updateWord = useUpdateWord();

  // Word exists check (for add mode)
  useEffect(() => {
    const timer = setTimeout(() => {
      setCheckEnabled(!isEdit && word.length >= 2 && languageCode.length >= 2);
    }, 500);
    return () => clearTimeout(timer);
  }, [word, languageCode, isEdit]);

  const { data: existsData, isLoading: isChecking } = useCheckWordExists(
    word,
    languageCode,
    checkEnabled
  );

  // AI generation hooks
  const generateAllPronunciations = useGenerateAllPronunciations();
  const generateImage = useGenerateImage();
  const generateWordMetadata = useGenerateWordMetadata();

  // Initialize form from existing word (edit mode)
  useEffect(() => {
    if (isEdit && existingWord) {
      setWord(existingWord.word);
      setLanguageCode(existingWord.languageCode);
      setImageUrl(existingWord.imageUrl || '');
      setExampleSentence(existingWord.exampleSentence || '');
      setSenseRank(existingWord.senseRank);
      setTranslations(existingWord.translations || {});
      setExampleTranslations(existingWord.exampleTranslations || {});
      setPronunciations(existingWord.pronunciations || {});
    }
  }, [isEdit, existingWord]);

  // Set default language in add mode
  useEffect(() => {
    if (!isEdit && contextLanguage) {
      setLanguageCode(contextLanguage);
    }
  }, [isEdit, contextLanguage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!word.trim()) {
      toast.error('Word is required');
      return;
    }

    // If word exists in DB, we're just adding it to a list - no metadata needed
    const isAddingExistingWord = !isEdit && existsData?.exists;

    if (!isAddingExistingWord) {
      if (!imageUrl.trim()) {
        toast.error('Image URL is required');
        return;
      }
      if (!exampleSentence.trim()) {
        toast.error('Example sentence is required');
        return;
      }
    }

    if (isEdit) {
      // Update existing word
      const data: WordUpdateRequest = {
        image_url: imageUrl.trim() || undefined,
        example_sentence: exampleSentence.trim() || undefined,
        translations: Object.keys(translations).length > 0 ? translations : undefined,
        example_translations:
          Object.keys(exampleTranslations).length > 0 ? exampleTranslations : undefined,
        pronunciations:
          Object.keys(pronunciations).length > 0
            ? Object.fromEntries(
                Object.entries(pronunciations).map(([locale, p]) => [
                  locale,
                  { ipa: p.ipa || undefined, audio_url: p.audioUrl || undefined },
                ])
              )
            : undefined,
        sense_rank: senseRank,
      };

      updateWord.mutate(
        {
          word: wordParam!,
          languageCode: langParam!,
          cardId: requestedCardId ?? existingWord?.cardId,
          data,
        },
        {
          onSuccess: () => {
            toast.success(`Word "${word}" updated successfully`);
            navigate(-1);
          },
          onError: (err) => {
            toast.error(`Failed to update word: ${(err as Error).message}`);
          },
        }
      );
    } else {
      // For existing words, only send word and language_code
      // For new words, send all metadata
      const data: AddWordToListRequest = isAddingExistingWord
        ? {
            card_id: existsData?.word?.cardId,
            word: word.trim(),
            language_code: languageCode,
          }
        : {
            word: word.trim(),
            language_code: languageCode,
            image_url: imageUrl.trim(),
            example_sentence: exampleSentence.trim(),
            translations: Object.keys(translations).length > 0 ? translations : undefined,
            example_translations:
              Object.keys(exampleTranslations).length > 0 ? exampleTranslations : undefined,
            pronunciations:
              Object.keys(pronunciations).length > 0
                ? Object.fromEntries(
                    Object.entries(pronunciations).map(([locale, p]) => [
                      locale,
                      { ipa: p.ipa || undefined, audio_url: p.audioUrl || undefined },
                    ])
                  )
                : undefined,
          };

      if (listId) {
        // Add to specific list
        addWordToList.mutate(
          { listId, data },
          {
            onSuccess: () => {
              toast.success(`Word "${word}" added to list`);
              navigate(`/words/lists/${listId}/words`);
            },
            onError: (err) => {
              toast.error(`Failed to add word: ${(err as Error).message}`);
            },
          }
        );
      } else {
        // Create standalone word
        createWord.mutate(data, {
          onSuccess: () => {
            toast.success(`Word "${word}" created`);
            navigate('/words');
          },
          onError: (err) => {
            toast.error(`Failed to create word: ${(err as Error).message}`);
          },
        });
      }
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Translation handlers
  const addTranslation = () => {
    if (newTransLang && newTransValue) {
      setTranslations({ ...translations, [newTransLang]: newTransValue });
      setNewTransLang('');
      setNewTransValue('');
    }
  };

  const removeTranslation = (lang: string) => {
    const updated = { ...translations };
    delete updated[lang];
    setTranslations(updated);
  };

  // Pronunciation handlers
  const addPronunciation = () => {
    if (newPronLocale) {
      setPronunciations({
        ...pronunciations,
        [newPronLocale]: { ipa: newPronIpa || null, audioUrl: null },
      });
      setNewPronLocale('');
      setNewPronIpa('');
    }
  };

  const removePronunciation = (locale: string) => {
    const updated = { ...pronunciations };
    delete updated[locale];
    setPronunciations(updated);
  };

  // AI Generation handlers
  const handleGenerateImage = () => {
    if (!word.trim()) {
      toast.error('Please enter a word first');
      return;
    }
    generateImage.mutate(
      {
        word: word.trim(),
        language_code: languageCode,
        ...(isEdit && (requestedCardId ?? existingWord?.cardId)
          ? { card_id: requestedCardId ?? existingWord?.cardId }
          : {}),
      },
      {
        onSuccess: (url) => {
          setImageUrl(url);
          toast.success('Image generated');
        },
        onError: (err) => {
          toast.error(`Failed to generate image: ${(err as Error).message}`);
        },
      }
    );
  };

  // Combined text metadata generation handler
  const handleGenerateTextMetadata = () => {
    if (!word.trim()) {
      toast.error('Please enter a word first');
      return;
    }
    generateWordMetadata.mutate(
      {
        word: word.trim(),
        language_code: languageCode,
        ...(isEdit && (requestedCardId ?? existingWord?.cardId)
          ? { card_id: requestedCardId ?? existingWord?.cardId }
          : {}),
      },
      {
        onSuccess: (result) => {
          setExampleSentence(result.exampleSentence);
          // Filter out zxx and empty translations
          const filterTranslations = (trans: Record<string, string>) =>
            Object.fromEntries(
              Object.entries(trans).filter(([code, value]) => code !== 'zxx' && value.trim())
            );
          const filteredWordTrans = filterTranslations(result.wordTranslations);
          const filteredExampleTrans = filterTranslations(result.exampleTranslations);
          setTranslations({ ...translations, ...filteredWordTrans });
          setExampleTranslations({ ...exampleTranslations, ...filteredExampleTrans });
          const transCount = Object.keys(filteredWordTrans).length;
          toast.success(`Generated example sentence and ${transCount} translation(s)`);
        },
        onError: (err) => {
          toast.error(`Failed to generate text metadata: ${(err as Error).message}`);
        },
      }
    );
  };

  const handleGenerateAllPronunciations = () => {
    if (!word.trim()) {
      toast.error('Please enter a word first');
      return;
    }
    if (!languageCode) {
      toast.error('Please select a language first');
      return;
    }
    generateAllPronunciations.mutate(
      {
        word: word.trim(),
        language_code: languageCode,
        generate_ipa: true,
        ...(isEdit && (requestedCardId ?? existingWord?.cardId)
          ? { card_id: requestedCardId ?? existingWord?.cardId }
          : {}),
      },
      {
        onSuccess: (result) => {
          // Merge generated pronunciations with existing ones
          const newPronunciations = { ...pronunciations };
          for (const pron of result.pronunciations) {
            newPronunciations[pron.locale] = {
              ipa: pron.ipa,
              audioUrl: pron.audioUrl,
            };
          }
          setPronunciations(newPronunciations);
          toast.success(`Generated ${result.totalGenerated} pronunciation(s)`);
        },
        onError: (err) => {
          toast.error(`Failed to generate pronunciations: ${(err as Error).message}`);
        },
      }
    );
  };

  // Loading state (edit mode)
  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Error state (edit mode)
  if (isEdit && error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading word</p>
          <p className="text-gray-600">{(error as Error).message}</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-blue-600 hover:text-blue-800">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // No language selected in add mode
  if (!isEdit && !languageCode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 text-lg mb-4">Please select a language first</p>
          <p className="text-gray-500">
            Use the language dropdown in the navigation to select a language.
          </p>
          <button
            onClick={() => navigate('/words')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Go to All Words
          </button>
        </div>
      </div>
    );
  }

  const isSubmitting = addWordToList.isPending || createWord.isPending || updateWord.isPending;
  const isGenerating =
    generateImage.isPending ||
    generateWordMetadata.isPending ||
    generateAllPronunciations.isPending;

  // Check if we're adding an existing word to a list
  const existingWordData = existsData?.exists ? existsData.word : null;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-4 text-sm">
          <Link to="/words" className="text-gray-500 hover:text-gray-700">
            Words
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900">{isEdit ? 'Edit Word' : 'Add Word'}</span>
        </div>

        <h1 className="text-3xl font-bold mb-6">{isEdit ? 'Edit Word' : 'Add Word'}</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Basic Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Word Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Word <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={word}
                    onChange={(e) => setWord(e.target.value)}
                    disabled={isEdit}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    placeholder="Enter word"
                    required
                  />
                  {isChecking && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                    </div>
                  )}
                </div>
              </div>

              {isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meaning rank
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={senseRank ?? ''}
                    onChange={(event) =>
                      setSenseRank(event.target.value ? Number(event.target.value) : null)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1"
                  />
                </div>
              )}

              {/* Language (read-only, set by navigation dropdown) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                <input
                  type="text"
                  value={getLanguageName(languageCode)}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-600"
                />
              </div>

              {/* Show existing word info when adding to list */}
              {existingWordData && !isEdit && (
                <div className="md:col-span-2 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-4">
                    {existingWordData.imageUrl && (
                      <img
                        src={existingWordData.imageUrl}
                        alt={existingWordData.word}
                        className="w-16 h-16 object-cover rounded-lg border"
                      />
                    )}
                    <div className="flex-1">
                      <p className="text-green-800 font-medium mb-1">
                        Word already exists in database
                      </p>
                      <p className="text-sm text-green-700">
                        This word will be added to the list with its existing metadata.
                      </p>
                      {existingWordData.exampleSentence && (
                        <p className="text-sm text-gray-600 mt-2 italic">
                          "{existingWordData.exampleSentence}"
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Image URL - only show for new words */}
              {!existingWordData && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image URL <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/image.png"
                      required={!existingWordData}
                    />
                    <button
                      type="button"
                      onClick={handleGenerateImage}
                      disabled={!word.trim() || !languageCode || generateImage.isPending}
                      className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-sm whitespace-nowrap"
                    >
                      {generateImage.isPending ? 'Generating...' : 'Generate'}
                    </button>
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt="Preview"
                        className="h-10 w-10 object-cover rounded border"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Text Metadata Card - only show for new words */}
          {!existingWordData && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Text Metadata</h2>
                <button
                  type="button"
                  onClick={handleGenerateTextMetadata}
                  disabled={!word.trim() || !languageCode || generateWordMetadata.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                >
                  {generateWordMetadata.isPending ? 'Generating...' : 'Generate All'}
                </button>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Click "Generate All" to create example sentence, word translations, and example
                translations in one request.
              </p>

              {/* Example Sentence */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Example Sentence <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={exampleSentence}
                  onChange={(e) => setExampleSentence(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter an example sentence using the word"
                  rows={2}
                  required
                />
              </div>

              {/* Word Translations */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Word Translations</h3>
                <div className="space-y-2">
                  {Object.entries(translations).map(([lang, trans]) => (
                    <div key={lang} className="flex items-center gap-2">
                      <span
                        className="w-20 text-sm font-medium text-gray-600 truncate"
                        title={lang}
                      >
                        {getLanguageName(lang)}
                      </span>
                      <input
                        type="text"
                        value={trans}
                        onChange={(e) =>
                          setTranslations({ ...translations, [lang]: e.target.value })
                        }
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeTranslation(lang)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {Object.keys(translations).length === 0 && (
                    <p className="text-sm text-gray-400 italic">No translations yet</p>
                  )}
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <select
                      value={newTransLang}
                      onChange={(e) => setNewTransLang(e.target.value)}
                      className="w-32 px-2 py-1.5 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="">Language...</option>
                      {languages
                        .filter((l) => l.code !== languageCode && !translations[l.code])
                        .map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                    </select>
                    <input
                      type="text"
                      value={newTransValue}
                      onChange={(e) => setNewTransValue(e.target.value)}
                      placeholder="Add translation manually"
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={addTranslation}
                      disabled={!newTransLang || !newTransValue}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Example Sentence Translations */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Example Sentence Translations
                </h3>
                <div className="space-y-2">
                  {Object.entries(exampleTranslations).map(([lang, trans]) => (
                    <div key={lang} className="flex items-center gap-2">
                      <span
                        className="w-20 text-sm font-medium text-gray-600 truncate"
                        title={lang}
                      >
                        {getLanguageName(lang)}
                      </span>
                      <input
                        type="text"
                        value={trans}
                        onChange={(e) =>
                          setExampleTranslations({ ...exampleTranslations, [lang]: e.target.value })
                        }
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updated = { ...exampleTranslations };
                          delete updated[lang];
                          setExampleTranslations(updated);
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {Object.keys(exampleTranslations).length === 0 && (
                    <p className="text-sm text-gray-400 italic">No example translations yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pronunciations Card - only show for new words */}
          {!existingWordData && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Pronunciations</h2>
                <button
                  type="button"
                  onClick={handleGenerateAllPronunciations}
                  disabled={!word.trim() || !languageCode || generateAllPronunciations.isPending}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 disabled:opacity-50"
                >
                  {generateAllPronunciations.isPending ? 'Generating...' : 'Generate All'}
                </button>
              </div>

              <div className="space-y-3">
                {Object.entries(pronunciations).map(([locale, pron]) => (
                  <div key={locale} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-bold text-gray-700">{locale}</span>
                      <span className="text-sm text-gray-500">
                        {locales.find((l) => l.code === locale)?.name || locale}
                      </span>
                      <button
                        type="button"
                        onClick={() => removePronunciation(locale)}
                        className="ml-auto text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                    {pron.ipa && (
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="text-gray-500">IPA:</span> {pron.ipa}
                      </div>
                    )}
                    {pron.audioUrl && (
                      <audio controls className="w-full h-8">
                        <source src={pron.audioUrl} type="audio/mpeg" />
                      </audio>
                    )}
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-2 border-t">
                  <select
                    value={newPronLocale}
                    onChange={(e) => setNewPronLocale(e.target.value)}
                    className="w-36 px-2 py-1.5 border border-gray-300 rounded-md bg-white"
                  >
                    <option value="">Locale...</option>
                    {locales
                      .filter((l) => l.language_code === languageCode && !pronunciations[l.code])
                      .map((locale) => (
                        <option key={locale.code} value={locale.code}>
                          {locale.name}
                        </option>
                      ))}
                  </select>
                  <input
                    type="text"
                    value={newPronIpa}
                    onChange={(e) => setNewPronIpa(e.target.value)}
                    placeholder="IPA transcription (optional)"
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={addPronunciation}
                    disabled={!newPronLocale}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (isGenerating && !existingWordData)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting
                ? 'Saving...'
                : isEdit
                  ? 'Save Changes'
                  : existingWordData
                    ? 'Add to List'
                    : 'Add Word'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
